import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, Question, Sparkle } from '@phosphor-icons/react';
import { listPortalFaqs, probarAsistente, type PortalFaq } from '../services/api';
import styles from './FaqPicker.module.css';

/**
 * «Pregunta frecuente» en el compositor del ticket (paso 51, K66): inserta el
 * texto VIGENTE de una pregunta frecuente en la respuesta, para revisarlo y
 * enviarlo. Son las «respuestas predefinidas» de W6 sin una segunda lista: el
 * mismo corpus que el asistente del portal.
 *
 * Arriba va la que mejor encaja con el ticket, con el MISMO `responder()` del
 * portal (vía «Probar», que no se apunta en los KPIs) y su mismo umbral: si no
 * llega, no se sugiere ninguna. Solo las activas.
 */
export function FaqPicker({
  token,
  textoTicket,
  onInsert,
}: {
  token: string;
  textoTicket: string;
  onInsert: (texto: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [faqs, setFaqs] = useState<PortalFaq[] | null>(null);
  const [sugerida, setSugerida] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const caja = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  // ⚠️ Posición FIJA, calculada desde el botón: la tarjeta «Actividad» recorta
  // lo que se sale de ella (`overflow: hidden`), y un desplegable absoluto se
  // cortaba por arriba (lo enseñó la captura). Se abre hacia ARRIBA si cabe
  // más arriba que abajo.
  const [pos, setPos] = useState<React.CSSProperties>({});
  const abrir = () => {
    const r = boton.current?.getBoundingClientRect();
    if (r) {
      const arriba = r.top > window.innerHeight - r.bottom;
      // Que no se salga por la derecha en una pantalla estrecha.
      const ancho = Math.min(420, window.innerWidth - 32);
      const left = Math.max(16, Math.min(r.left, window.innerWidth - ancho - 16));
      setPos(arriba
        ? { left, bottom: window.innerHeight - r.top + 6, maxHeight: Math.min(360, r.top - 16) }
        : { left, top: r.bottom + 6, maxHeight: Math.min(360, window.innerHeight - r.bottom - 16) });
    }
    setAbierto((v) => !v);
  };

  useEffect(() => {
    if (!abierto || faqs !== null) return;
    (async () => {
      try {
        const lista = await listPortalFaqs(token);
        setFaqs(lista.items.filter((f) => f.activa));
        const r = await probarAsistente(token, textoTicket.slice(0, 500)).catch(() => null);
        if (r?.resultado === 'faq' && r.faqId) setSugerida(r.faqId);
      } catch (e) {
        setError((e as Error).message);
        setFaqs([]);
      }
    })();
  }, [abierto, faqs, token, textoTicket]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto]);

  const visibles = useMemo(() => {
    const n = q.trim().toLowerCase();
    const lista = (faqs ?? []).filter((f) => !n || [f.pregunta, ...f.variantes].join(' ').toLowerCase().includes(n));
    return [...lista].sort((a, b) => Number(b.id === sugerida) - Number(a.id === sugerida));
  }, [faqs, q, sugerida]);

  const vigente = (f: PortalFaq) => f.versiones.find((v) => v.n === f.versionActual)?.respuesta ?? '';

  return (
    <div className={styles.wrap} ref={caja}>
      <button
        type="button"
        ref={boton}
        className={styles.trigger}
        onClick={abrir}
        aria-expanded={abierto}
        aria-haspopup="dialog"
      >
        <Question size={14} weight="bold" /> Pregunta frecuente
      </button>
      {abierto && (
        <div className={styles.pop} style={pos} role="dialog" aria-label="Insertar una pregunta frecuente">
          <label className={styles.search}>
            <MagnifyingGlass size={14} weight="bold" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pregunta" aria-label="Buscar pregunta frecuente" />
          </label>
          <div className={styles.lista}>
            {faqs === null && <p className={styles.vacio}>Cargando…</p>}
            {error && <p className={styles.vacio}>{error}</p>}
            {faqs !== null && !error && visibles.length === 0 && (
              <p className={styles.vacio}>{faqs.length === 0 ? 'No hay preguntas frecuentes activas.' : 'Ninguna coincide.'}</p>
            )}
            {visibles.map((f) => (
              <button
                key={f.id}
                type="button"
                className={styles.item}
                onClick={() => { onInsert(vigente(f)); setAbierto(false); }}
              >
                <span className={styles.itemTop}>
                  <span className={styles.itemPregunta}>{f.pregunta}</span>
                  {f.id === sugerida && <span className={styles.chip}><Sparkle size={10} weight="fill" /> Encaja con este ticket</span>}
                </span>
                <span className={styles.itemTexto}>{vigente(f)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
